import { useCompanyDepartmentsClient } from '@frontend/clients/company-department.request'
import { useDepartmentLimitClient } from '@frontend/clients/department.request'
import { useFacilityDepartmentsClient } from '@frontend/clients/facility-department.request'
import { useDebounce } from '@frontend/components/hooks'
import { useQuery } from '@tanstack/react-query'

import { ListDepartmentLimitsFilters } from '@shared-types/calendar'
import {
  CompanyListDepartmentsFilterParam,
  DepartmentsListFilterParam,
} from '@shared-types/department'
import { AccessibleDepartmentFilterParams } from '@shared-types/domain'
import { Identifier, Nullable } from '@shared-types/util'

export function useQueryCompanyDepartments(
  companyId?: Identifier,
  params?: CompanyListDepartmentsFilterParam
) {
  const companyDepartmentsClient = useCompanyDepartmentsClient()
  const debouncedFilters = useDebounce(params)

  return useQuery({
    queryKey: ['companies', companyId, 'departments', debouncedFilters],

    queryFn: async () => {
      if (!companyId) {
        return {
          results: [],
        }
      }
      return companyDepartmentsClient.listDepartments(companyId, debouncedFilters)
    },
  })
}

export function useQueryEnhancedDepartment(
  companyId?: Identifier,
  facilityId?: Nullable<Identifier>,
  departmentId?: Nullable<Identifier>
) {
  const facilityDepartmentsClient = useFacilityDepartmentsClient()

  return useQuery({
    queryKey: ['companies', companyId, 'departments', departmentId],

    queryFn: async () => {
      if (!companyId || !facilityId || !departmentId) {
        return null
      }
      return facilityDepartmentsClient.getEnhancedDepartment(companyId, facilityId, departmentId)
    },
  })
}

export function useQueryCompanyFacilityDepartments(
  companyId?: Identifier,
  facilityId?: Nullable<Identifier>,
  params?: DepartmentsListFilterParam
) {
  const facilityDepartmentsClient = useFacilityDepartmentsClient()
  const debouncedFilters = useDebounce(params)

  return useQuery({
    queryKey: ['companies', companyId, 'facilities', facilityId, 'departments', debouncedFilters],

    queryFn: async () => {
      if (!companyId || !facilityId) {
        return {
          results: [],
        }
      }
      return facilityDepartmentsClient.listDepartments(companyId, facilityId, debouncedFilters)
    },
  })
}

export function useQueryAccessibleDepartments(
  companyId: Identifier,
  params?: AccessibleDepartmentFilterParams
) {
  const facilityDepartmentsClient = useFacilityDepartmentsClient()
  const debouncedFilters = useDebounce(params)

  return useQuery({
    queryKey: ['companies', companyId, 'departments', debouncedFilters],

    queryFn: async () => {
      return facilityDepartmentsClient.listAccessibleDepartments(companyId, debouncedFilters)
    },
  })
}

export function useQueryAllCompanyFacilityDepartments(
  companyId?: Identifier,
  facilityId?: Nullable<Identifier>
) {
  const facilityDepartmentsClient = useFacilityDepartmentsClient()

  return useQuery({
    queryKey: ['companies', companyId, 'facilities', facilityId, 'departments', 'all'],

    queryFn: async () => {
      if (!companyId || !facilityId) {
        return []
      }
      return facilityDepartmentsClient.listAllDepartments(companyId, facilityId)
    },
  })
}

export function useQueryDepartmentAutomation(
  companyId?: Identifier,
  facilityId?: Nullable<Identifier>,
  departmentId?: Nullable<Identifier>
) {
  const facilityDepartmentsClient = useFacilityDepartmentsClient()

  return useQuery({
    queryKey: [
      'companies',
      companyId,
      'facilities',
      facilityId,
      'departments',
      departmentId,
      'automation',
    ],

    queryFn: async () => {
      if (!companyId || !facilityId || !departmentId) {
        return null
      }
      return facilityDepartmentsClient.getDepartmentAutomation(companyId, facilityId, departmentId)
    },
  })
}

export function useQueryDepartmentServicePrices(
  companyId?: Identifier,
  facilityId?: Nullable<Identifier>,
  departmentId?: Nullable<Identifier>
) {
  const facilityDepartmentsClient = useFacilityDepartmentsClient()

  return useQuery({
    queryKey: [
      'companies',
      companyId,
      'facilities',
      facilityId,
      'departments',
      departmentId,
      'service_prices',
    ],

    queryFn: async () => {
      if (!companyId || !facilityId || !departmentId) {
        return []
      }
      return facilityDepartmentsClient.listServicePrices(companyId, facilityId, departmentId)
    },
  })
}

export function useQueryDepartmentLimits(
  departmentId?: Identifier,
  filters?: ListDepartmentLimitsFilters
) {
  const departmentLimitClient = useDepartmentLimitClient()
  const debouncedFilters = useDebounce(filters)

  return useQuery({
    queryKey: ['departments', departmentId, 'limits', debouncedFilters],

    queryFn: async () => {
      if (!departmentId) {
        return {
          results: [],
        }
      }
      return departmentLimitClient.list(departmentId, debouncedFilters)
    },
  })
}
